<template>
  <div class="admin-noticeboard p-4 bg-white rounded-lg shadow-md relative flex flex-col gap-4 h-full">
    <div class="justify-between flex border-b-2 pb-2 items-center">
      <span class="text-lg font-bold text-primary">
        VDR Requests
      </span>
      <span
        v-if="!isLoading && openCount > 0"
        class="text-sm text-gray-600"
      >
        {{ openCount }} open
        <template v-if="highCount > 0">
          · <span class="text-red-500 font-medium">⚠ {{ highCount }} high</span>
        </template>
      </span>
    </div>

    <div
      v-if="!isLoading"
      class="overflow-y-auto show-y-thin-scrollbar px-1 flex-auto"
    >
      <div
        v-if="items.length === 0"
        class="flex items-center justify-center h-full"
      >
        <p class="text-gray-500 text-center">
          No open requests
        </p>
      </div>
      <div
        v-else
        class="flex-auto space-y-4"
      >
        <div
          v-for="group in groups"
          :key="group.businessId"
        >
          <!-- Deal header — businessName shown once per group; click jumps to this deal's VDR. -->
          <button
            type="button"
            class="w-full flex items-center gap-2 mb-2 text-left"
            @click="goToRequest(group.businessId)"
          >
            <span class="text-sm font-semibold text-gray-900 truncate flex-1">
              {{ group.businessName || 'Untitled deal' }}
            </span>
            <span class="text-xs text-gray-400 shrink-0">
              {{ group.items.length }}
            </span>
          </button>
          <div class="space-y-2">
            <div
              v-for="item in group.items"
              :key="item.rowId"
              class="flex items-start gap-2 p-3 border rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
              @click="goToRequest(item.businessId)"
            >
              <span
                class="w-2 h-2 mt-1.5 rounded-full shrink-0"
                :class="priorityDotClass(item.priority)"
              />
              <p class="text-sm text-gray-600 line-clamp-2 flex-1">
                {{ item.request }}
              </p>
              <span class="text-xs text-gray-400 shrink-0">
                {{ formatAge(item.requestedAt) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex items-center justify-center h-full"
    >
      <ProgressSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useDashboard } from '~/composables/useDashboardCA'

// Broker dashboard widget: open VDR requests across the broker's deals, so they notice new
// requests without opening each deal's Requests tab. Data + role scoping come from
// /api/v1/dashboard/vdr-requests-summary; a row click jumps to that deal's VDR.
interface OpenRequestItem {
  rowId: string
  businessId: string
  businessName: string
  request: string
  priority: string | null
  requestedAt: string | null
}

const { getVdrRequestsSummary } = useDashboard()
const toast = useToast()

const isLoading = ref(true)
const openCount = ref(0)
const highCount = ref(0)
const items = ref<OpenRequestItem[]>([])

// Group the open requests by deal. The server returns them oldest-first, so iterating in
// order yields groups in "deal with the oldest request first" order, and each group keeps its
// items oldest-first — no extra sorting needed here.
const groups = computed(() => {
  const byDeal = new Map<string, { businessId: string, businessName: string, items: OpenRequestItem[] }>()
  for (const item of items.value) {
    const existing = byDeal.get(item.businessId)
    if (existing) existing.items.push(item)
    else byDeal.set(item.businessId, { businessId: item.businessId, businessName: item.businessName, items: [item] })
  }
  return [...byDeal.values()]
})

const init = async () => {
  isLoading.value = true
  try {
    const res = await getVdrRequestsSummary() as {
      data?: { openCount: number, highCount: number, items: OpenRequestItem[] }
    }
    if (res?.data) {
      openCount.value = res.data.openCount
      highCount.value = res.data.highCount
      items.value = res.data.items
    }
  }
  catch (error) {
    console.error('Error fetching VDR requests:', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch VDR requests', life: 3000 })
  }
  finally {
    isLoading.value = false
  }
}

// Priority dot — High red, Medium amber, Low/none grey.
const priorityDotClass = (priority: string | null) => {
  if (priority === 'High') return 'bg-red-500'
  if (priority === 'Medium') return 'bg-amber-500'
  if (priority === 'Low') return 'bg-gray-400'
  return 'bg-gray-300'
}

// Compact relative age (m/h/d) from the request timestamp.
const formatAge = (iso: string | null) => {
  if (!iso) return ''
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

// Deep-link straight to the deal's Requests tab — vdr.vue reads ?tab to pick the initial tab.
const goToRequest = (businessId: string) => {
  navigateTo(`/businesses/${businessId}/details/vdr?tab=requests`)
}

onMounted(init)
</script>
